/**
 * La scheda «Aziende»: le aziende registrate dai partner o abbinate a mano, col loro stato. Qui Simone decide i
 * doppioni (accetta o rifiuta col motivo, che il partner legge) e abbina a mano chi è arrivato prima dell'accesso.
 *
 * Per le aziende portate da un FORNITORE mostra anche l'esclusiva (cap. 444: i primi mesi dall'iscrizione l'azienda è
 * sua), che calcola il server, e qui Simone la allunga, accorcia, toglie o la riporta alla regola.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, InputAdornment, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { SoftChip, type SoftChipTone } from '../SoftChip';
import type { AbbinamentoDoc, StatoAbbinamento } from '@shared/partner/tipi';
import {
  decidiEsclusiva, decidiRegistrazione, leggiEsclusive, messaggioDiErrore, type ConId, type DatiProgrammaPartner, type EsclusiveDeiFornitori,
} from '../../services/partnerConsoleService';
import { DialogoAbbina } from './DialogoAbbina';
import { DialogoCampi } from './DialogoCampi';
import type { Esegui } from './esegui';
import { oggi, useFormati } from './formati';
import { ScalaCompenso } from './ScalaCompenso';
import { meseDiAbbonamento } from '@shared/partner/compensoPartner';

const STATI: StatoAbbinamento[] = ['da_decidere', 'registrata', 'attiva', 'rifiutata', 'scaduta'];
export const TONO_ABBINAMENTO: Record<StatoAbbinamento, SoftChipTone> = {
  registrata: 'info',
  da_decidere: 'warning',
  attiva: 'success',
  rifiutata: 'error',
  scaduta: 'default',
};

interface Props {
  dati: DatiProgrammaPartner;
  esegui: Esegui;
  onErrore: (messaggio: string) => void;
  onRicarica: () => void;
}

export const SchedaAziende: React.FC<Props> = ({ dati, esegui, onErrore, onRicarica }) => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const [filtroStato, setFiltroStato] = useState<StatoAbbinamento | 'tutti'>('tutti');
  const [cerca, setCerca] = useState('');
  const [abbina, setAbbina] = useState(false);
  const [daRifiutare, setDaRifiutare] = useState<ConId<AbbinamentoDoc> | null>(null);
  const [inEsclusiva, setInEsclusiva] = useState<ConId<AbbinamentoDoc> | null>(null);
  const [esclusive, setEsclusive] = useState<EsclusiveDeiFornitori | null>(null);
  const [erroreEsclusive, setErroreEsclusive] = useState<string | null>(null);

  // Le esclusive le calcola il server (chi è un fornitore, da quando è iscritta l'azienda): si rileggono a ogni ricarica.
  const caricaEsclusive = useCallback(() => {
    leggiEsclusive()
      .then((e) => { setEsclusive(e); setErroreEsclusive(null); })
      .catch((err) => setErroreEsclusive(messaggioDiErrore(err)));
  }, []);
  useEffect(() => { caricaEsclusive(); }, [caricaEsclusive, dati.abbinamenti]);
  const decidi = (abbinamentoId: string, finoAl: string | null, motivo?: string) =>
    esegui(() => decidiEsclusiva(abbinamentoId, finoAl, motivo).finally(caricaEsclusive));

  const nomePartner = useMemo(() => new Map(dati.partner.map((p) => [p.id, p.nome])), [dati.partner]);

  const elenco = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    return dati.abbinamenti
      .filter((a) => filtroStato === 'tutti' || a.stato === filtroStato)
      .filter((a) => !q || [a.azienda_nome, a.partita_iva_chiave, nomePartner.get(a.partner_id) ?? ''].some((x) => x.toLowerCase().includes(q)))
      .sort((a, b) => STATI.indexOf(a.stato) - STATI.indexOf(b.stato) || b.registrata_il.localeCompare(a.registrata_il));
  }, [dati.abbinamenti, filtroStato, cerca, nomePartner]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: tokens.color.line, bgcolor: tokens.color.paper }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            placeholder={t('filtri.cerca')}
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: tokens.color.mist }} /></InputAdornment> }}
          />
          <TextField select size="small" value={filtroStato} onChange={(e) => setFiltroStato(e.target.value as StatoAbbinamento | 'tutti')} sx={{ minWidth: 190 }}>
            <MenuItem value="tutti">{t('filtri.tutti_gli_stati')}</MenuItem>
            {STATI.map((s) => <MenuItem key={s} value={s}>{t(`stato_abbinamento.${s}`)}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAbbina(true)} sx={{ textTransform: 'none', flexShrink: 0 }}>
            {t('azioni.abbina')}
          </Button>
        </Stack>
      </Paper>

      {erroreEsclusive && (
        <Typography variant="body2" sx={{ mb: 1.5, color: tokens.color.ash }}>{t('aziende.esclusive_non_lette', { motivo: erroreEsclusive })}</Typography>
      )}
      <Paper variant="outlined" sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}>
        {elenco.length === 0 ? (
          <Typography variant="body2" sx={{ p: 4, color: tokens.color.ash, textAlign: 'center' }}>{t('vuoti.aziende')}</Typography>
        ) : (
          <Table size="small" sx={{ '& td': { py: 1.5 }, '& tr:last-child td': { borderBottom: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('colonne.azienda')}</TableCell>
                <TableCell>{t('colonne.partner')}</TableCell>
                <TableCell>{t('colonne.stato')}</TableCell>
                <TableCell>{t('colonne.registrata_il')}</TableCell>
                <TableCell>{t('colonne.abbonata_dal')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {elenco.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite }}>{a.azienda_nome}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.ash }}>{t('aziende.partita_iva', { chiave: a.partita_iva_chiave })}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{nomePartner.get(a.partner_id) ?? a.partner_id}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.color.mist }}>{t(`origine.${a.origine}`)}</Typography>
                    {esclusive?.esclusive[a.id] && (() => {
                      const e = esclusive.esclusive[a.id];
                      return (
                        <Box sx={{ mt: 0.75 }}>
                          <SoftChip
                            label={t(e.attiva ? 'aziende.esclusiva_attiva' : 'aziende.esclusiva_finita', { fornitore: e.fornitore_nome, data: f.giorno(e.fino_al) })}
                            tone={e.attiva ? 'info' : 'default'}
                          />
                          {e.a_mano && <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.25 }}>{t('aziende.esclusiva_a_mano')}</Typography>}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <SoftChip label={t(`stato_abbinamento.${a.stato}`)} tone={TONO_ABBINAMENTO[a.stato]} />
                    {a.stato === 'registrata' && a.in_lista_il && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.5 }}>{t('aziende.in_lista')}</Typography>
                    )}
                    {a.motivo && <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block', mt: 0.5, maxWidth: 260 }}>{a.motivo}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{f.giorno(a.registrata_il)}</Typography>
                    {a.stato === 'registrata' && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist }}>{t('aziende.scade', { data: f.giorno(a.scade_il) })}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{a.abbonata_dal ? f.giorno(a.abbonata_dal) : t('aziende.mai_pagato')}</Typography>
                    {a.stato === 'attiva' && a.abbonata_dal && (
                      <Box sx={{ mt: 1, maxWidth: 240 }}>
                        <ScalaCompenso scaglioni={a.scaglioni} meseDiOggi={meseDiAbbonamento(a.abbonata_dal, oggi())} compatta />
                      </Box>
                    )}
                    {a.stato === 'attiva' && (
                      <Typography variant="caption" sx={{ color: tokens.color.mist }}>{t('aziende.contare_dal', { data: f.giorno(a.contare_dal) })}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {a.stato === 'da_decidere' && (
                        <Button size="small" variant="outlined" onClick={() => void esegui(() => decidiRegistrazione(a.id, 'accetta'))} sx={{ textTransform: 'none' }}>
                          {t('azioni.accetta')}
                        </Button>
                      )}
                      {esclusive?.esclusive[a.id] && (
                        <Button size="small" variant="outlined" onClick={() => setInEsclusiva(a)} sx={{ textTransform: 'none' }}>
                          {t('azioni.esclusiva')}
                        </Button>
                      )}
                      {esclusive?.esclusive[a.id]?.a_mano && (
                        <Button size="small" onClick={() => void decidi(a.id, null)} sx={{ textTransform: 'none' }}>
                          {t('azioni.esclusiva_regola', { mesi: esclusive.mesi })}
                        </Button>
                      )}
                      {(a.stato === 'da_decidere' || a.stato === 'registrata' || a.stato === 'attiva') && (
                        <Button size="small" color="error" onClick={() => setDaRifiutare(a)} sx={{ textTransform: 'none' }}>
                          {t('azioni.rifiuta')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <DialogoAbbina
        aperto={abbina}
        partner={dati.partner}
        abbinamenti={dati.abbinamenti}
        onChiudi={() => setAbbina(false)}
        onSalvato={onRicarica}
        onErrore={onErrore}
      />
      <DialogoCampi
        aperto={!!inEsclusiva}
        titolo={t('dialogo_esclusiva.titolo', { azienda: inEsclusiva?.azienda_nome ?? '' })}
        campi={[
          { nome: 'fino_al', etichetta: t('dialogo_esclusiva.fino_al'), tipo: 'data', obbligatorio: true, aiuto: t('dialogo_esclusiva.fino_al_aiuto'), iniziale: inEsclusiva ? esclusive?.esclusive[inEsclusiva.id]?.fino_al : undefined },
          { nome: 'motivo', etichetta: t('dialogo_esclusiva.motivo'), tipo: 'testo_lungo' },
        ]}
        etichettaConferma={t('dialogo_esclusiva.salva')}
        onConferma={(v) => (inEsclusiva ? decidi(inEsclusiva.id, v.fino_al, v.motivo.trim() || undefined) : Promise.resolve(false))}
        onChiudi={() => setInEsclusiva(null)}
      />
      <DialogoCampi
        aperto={!!daRifiutare}
        titolo={t('dialogo_motivo.rifiuta_titolo', { azienda: daRifiutare?.azienda_nome ?? '' })}
        campi={[{ nome: 'motivo', etichetta: t('dialogo_motivo.motivo'), tipo: 'testo_lungo', obbligatorio: true, aiuto: t('dialogo_motivo.motivo_aiuto') }]}
        etichettaConferma={t('azioni.rifiuta')}
        onConferma={(v) => (daRifiutare ? esegui(() => decidiRegistrazione(daRifiutare.id, 'rifiuta', v.motivo.trim())) : Promise.resolve(false))}
        onChiudi={() => setDaRifiutare(null)}
      />
    </Box>
  );
};
